import React, { useRef, useState } from 'react';
import { Box, Button, CircularProgress, Alert } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme } from '@mui/material/styles';

interface CameraCaptureProps {
  onCapture: (file: File, previewUrl: string) => void;
  onClose: () => void;
}

const CameraCapture: React.FC<CameraCaptureProps> = ({ onCapture, onClose }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [error, setError] = useState<string | null>(null);

  const startCamera = React.useCallback(async () => {
    try {
      setError(null);
      setIsLoading(true);
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        await new Promise((resolve) => {
          if (videoRef.current) {
            videoRef.current.onloadedmetadata = () => {
              resolve(true);
            };
          }
        });
        
        await videoRef.current.play();
      }
    } catch (err) {
      let errorMessage = 'Erro ao acessar a câmera.';
      
      if (err instanceof Error) {
        if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          errorMessage = 'Nenhuma câmera foi encontrada no dispositivo.';
        } else if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          errorMessage = 'Permissão para usar a câmera foi negada. Por favor, permita o acesso à câmera.';
        } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
          errorMessage = 'Sua câmera pode estar sendo usada por outro aplicativo.';
        }
      }

      console.error('Erro ao acessar câmera:', errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    startCamera();
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [startCamera]);

  const capturePhoto = () => {
    if (!videoRef.current) return;

    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    
    const context = canvas.getContext('2d');
    if (context) {
      context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      
      // Converter para base64
      const imageBase64 = canvas.toDataURL('image/jpeg');
      
      // Criar um blob para simular um arquivo
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], 'camera-photo.jpg', { type: 'image/jpeg' });
          onCapture(file, imageBase64);
        }
      }, 'image/jpeg');
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center',
      gap: 2 
    }}>
      {error && (
        <Alert 
          severity="error"
          sx={{ width: '100%', maxWidth: '600px' }}
          action={
            <Button color="inherit" size="small" onClick={onClose}>
              Fechar
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {!error && (
        <>
          <Box sx={{ 
            width: '100%', 
            maxWidth: '600px', 
            position: 'relative',
            aspectRatio: '16/9',
            overflow: 'hidden',
            borderRadius: '4px',
            border: '1px solid #ccc',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#f5f5f5'
          }}>
            {isLoading && (
              <CircularProgress />
            )}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: isLoading ? 'none' : 'block'
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={capturePhoto}
              fullWidth={isMobile}
              disabled={isLoading}
            >
              Capturar Foto
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={onClose}
              fullWidth={isMobile}
            >
              Cancelar
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default CameraCapture; 